import React, { useRef, useState } from 'react';
import { holeToken } from '../../lib/sitzung';
import { MIcon } from '../../lib/micon';
import { API_BASE, apiPost, apiFetch, zeigeApiFehler } from '../../lib/api';
import { schuljahrFenster } from '../../lib/schuljahr';
import { note6Rueckfrage, BestaetigteNote6 } from '../fehlzeiten/note6-rueckfrage';
import { useEscapeKey, useIsMobile } from '../../lib/hooks';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { KlassenbuchEintrag } from '../../types';
import { LdSheet } from '../../components/ui/LdSheet';
import { mikrofonAnfragen, zeigeMikrofonFehler, fokussiere } from '../../lib/mikrofon';
import { useMitarbeitImEintrag, MitarbeitImEintrag } from './MitarbeitImEintrag';
import { PortalMenu } from '../klasse/KlassenKopf';
import { wtKommaTm } from '../schueler/profil/format';
import './klassenbuch-k2.css';

// ── Welle K2: Fenster „Stunde eintragen“ (vorher KlassenbuchEintragModal) ─────────────
// Gleiche Daten und Abläufe wie seit B3: ein Eintrag je Klasse, Datum und Stunde (POST /api/klassenbuch),
// Fehlende werden auf dem Server zu Fehlzeiten, Mitarbeit danach über den Speicherweg der Mitarbeit.
// Neu ist nur die Ordnung: Kopf „Mathe, 4. Stunde“ (Fach antippbar), Thema und Hausaufgabe mit Mikro,
// Anmerkung eingeklappt, Fehlende als Chips (Handy) bzw. Raster aller Kinder (PC), Fußleiste.
// Handy: volles Fenster (LdSheet). PC: rechts neben der Woche (inline).

export type SpeichernArt = 'normal' | 'weiter';

const kurzname = (name: string) => {
  const [v, ...rest] = name.split(' ');
  const n = rest.join(' ');
  return n ? `${v} ${n[0]}.` : v;
};

export function StundeEintragen({
  stunde, datum, klasse_id, klasseName, zeit, eintrag, schuelerListe, faecher, onSave, onClose, inline = false,
  vorausgefuellt, fachVorschlag, onGeloescht, mitWeiter = false,
}: {
  stunde: number;
  datum: string;
  klasse_id: number | null;
  klasseName?: string | null;
  zeit?: string | null;                       // „10:15 bis 11:00“
  eintrag: KlassenbuchEintrag | null;
  schuelerListe: { id: number; name: string }[];
  faecher: string[];
  onSave: (e: KlassenbuchEintrag, art: SpeichernArt) => void;
  onClose: () => void;
  inline?: boolean;
  vorausgefuellt?: { thema?: string; hausaufgaben?: string; anmerkungen?: string; abwesende_schueler?: string[] };
  fachVorschlag?: string | null;
  onGeloescht?: (id: number) => void;   // Welle U6a
  mitWeiter?: boolean;                   // PC: „Speichern und nächste offene Stunde“
}) {
  useEscapeKey(inline ? () => {} : onClose);
  const isMobile = useIsMobile();
  const fehlendIds: number[]      = eintrag ? JSON.parse(eintrag.fehlende_schueler)      : [];
  const entschuldIds: number[]    = eintrag ? JSON.parse(eintrag.entschuldigte_schueler)  : [];

  const [fach,          setFach]          = useState(eintrag?.fach          ?? fachVorschlag ?? faecher[0] ?? '');
  const [thema,         setThema]         = useState(eintrag?.thema         ?? vorausgefuellt?.thema         ?? '');
  const [hausaufgaben,  setHausaufgaben]  = useState(eintrag?.hausaufgaben  ?? vorausgefuellt?.hausaufgaben  ?? '');
  const [anmerkungen,   setAnmerkungen]   = useState(eintrag?.anmerkungen   ?? vorausgefuellt?.anmerkungen   ?? '');
  const [anmerkungOffen, setAnmerkungOffen] = useState(!!(eintrag?.anmerkungen || vorausgefuellt?.anmerkungen));
  function nameMatcht(slName: string, kiName: string): boolean {
    const norm = (s: string) => s.toLowerCase().trim().replace(/[-\s]+/g, ' ').trim();
    const sl = norm(slName);
    const ki = norm(kiName);
    if (!ki) return false;
    if (sl === ki || sl.includes(ki) || ki.includes(sl)) return true;
    const teile = sl.split(' ');
    return teile.some(t => t.length > 1 && (t === ki || ki === t || t.startsWith(ki) || ki.startsWith(t)));
  }
  const vorausFehlendIds: number[] = !eintrag && vorausgefuellt?.abwesende_schueler?.length
    ? schuelerListe.filter(sl => vorausgefuellt.abwesende_schueler!.some(n => nameMatcht(sl.name, n))).map(sl => sl.id)
    : [];
  const [fehlend,       setFehlend]       = useState<Set<number>>(new Set(fehlendIds.length ? fehlendIds : vorausFehlendIds));
  const [entschuldigt,  setEntschuldigt]  = useState<Set<number>>(new Set(entschuldIds));
  const [laden,         setLaden]         = useState(false);
  const [fehler,        setFehler]        = useState('');
  const [diktatLaeuft,  setDiktatLaeuft]  = useState(false);
  // Welle B3: Mitarbeit (optional) — gespeichert nach dem Eintrag, über den Speicherweg der Mitarbeit
  const mitarbeit = useMitarbeitImEintrag(klasse_id, fach, datum, true);
  const [mitarbeitFehler, setMitarbeitFehler] = useState<Record<number, string>>({});
  const [diktatLaden,   setDiktatLaden]   = useState(false);
  const diktatRecorderRef = useRef<MediaRecorder | null>(null);
  // Welle K2: Fach im Kopf ändern (z. B. Vertretung) — dieselbe Wahl wie bisher (Fächer der Klasse oder frei)
  const [fachMenu, setFachMenu] = useState(false);
  const [fachFrei, setFachFrei] = useState(!!fach && faecher.length > 0 && !faecher.includes(fach));
  const fachKnopf = useRef<HTMLButtonElement>(null);
  const [kindMenu, setKindMenu] = useState<{ id: number; el: HTMLElement } | null>(null);
  const [auswahlOffen, setAuswahlOffen] = useState(false);
  const raster = !isMobile;   // PC: Raster aller Kinder; Handy: Chips + „Fehlende wählen“

  function toggleFehlend(id: number) {
    setFehlend(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
    if (fehlend.has(id)) {
      setEntschuldigt(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  }

  function setzeEntschuldigt(id: number, ja: boolean) {
    if (!fehlend.has(id)) return;
    setEntschuldigt(prev => {
      const n = new Set(prev);
      if (ja) n.add(id); else n.delete(id);
      return n;
    });
  }

  async function speichern(art: SpeichernArt = 'normal') {
    if (!fach.trim()) { setFehler('Bitte ein Fach angeben.'); return; }
    if (!klasse_id)   { setFehler('Keine Klasse ausgewählt.'); return; }
    setLaden(true); setFehler('');
    try {
      const body = {
        klasse_id, datum, stunde, fach: fach.trim(),
        thema:         thema.trim()        || null,
        hausaufgaben:  hausaufgaben.trim()  || null,
        anmerkungen:   anmerkungen.trim()   || null,
        fehlende_schueler:      Array.from(fehlend),
        entschuldigte_schueler: Array.from(entschuldigt),
      };
      const data = await apiPost<any>('/api/klassenbuch', body);
      const nMitarbeit = mitarbeit.aenderungen(fehlend).length;
      const fehl = nMitarbeit ? await mitarbeit.speichern(fehlend) : {};
      setMitarbeitFehler(fehl);
      if (Object.keys(fehl).length) {
        // Eintrag ist gespeichert, die Mitarbeit einzelner Kinder nicht → Fenster bleibt offen
        setFehler(`Eintrag gespeichert, aber ${Object.keys(fehl).length} Mitarbeits-Wert(e) nicht. Bitte prüfen und erneut speichern.`);
        return;
      }
      toast(nMitarbeit ? `Klassenbuch gespeichert, Mitarbeit für ${nMitarbeit} ${nMitarbeit === 1 ? 'Kind' : 'Kinder'}` : 'Klassenbuch gespeichert', 'success');
      onSave(data, art);
    } catch (e: any) {
      setFehler(e.message ?? 'Speichern fehlgeschlagen.');
    } finally {
      setLaden(false);
    }
  }

  // Welle U6a: Eintrag löschen — verknüpfte Auto-Fehlzeiten gehen mit (Backend, Fehlzeiten-Helfer)
  const sj = schuljahrFenster();
  const loeschbar = !!eintrag && !!onGeloescht && eintrag.datum >= sj.von && eintrag.datum < sj.bisExkl;
  async function eintragLoeschen() {
    if (!eintrag) return;
    const n = eintrag.fehlzeiten_anzahl ?? 0;
    const tag = new Date(eintrag.datum + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
    const text = `Der Eintrag vom ${tag}, ${eintrag.stunde}. Stunde wird gelöscht.` +
      (n > 0 ? ` ${n} ${n === 1 ? 'Fehlzeit' : 'Fehlzeiten'} aus diesem Eintrag ${n === 1 ? 'wird' : 'werden'} ebenfalls gelöscht.` : '');
    if (!inline) onClose();   // modales Sheet zuerst schließen
    if (!(await confirm(text, { title: 'Klassenbuch-Eintrag löschen?', confirmLabel: 'Löschen', danger: true }))) return;
    try {
      const r = await apiFetch<{ fehlzeiten_geloescht: number; bestaetigte_note6: BestaetigteNote6[] }>(`/api/klassenbuch/${eintrag.id}`, { method: 'DELETE' });
      toast(r.fehlzeiten_geloescht > 0 ? `Eintrag gelöscht, ${r.fehlzeiten_geloescht} ${r.fehlzeiten_geloescht === 1 ? 'Fehlzeit' : 'Fehlzeiten'} entfernt` : 'Eintrag gelöscht', 'success');
      window.dispatchEvent(new Event('lehrerdesk-schueler-reload'));
      onGeloescht!(eintrag.id);
      await note6Rueckfrage(r.bestaetigte_note6);
    } catch (err) { zeigeApiFehler(err, 'Löschen fehlgeschlagen'); }
  }

  // Diktat füllt Thema, Hausaufgabe und Anmerkung aus einer Aufnahme (wie bisher; Mikro in beiden Feldern)
  async function startDiktat() {
    if (diktatLaeuft) {
      diktatRecorderRef.current?.stop();
      return;
    }
    if ((thema.trim() || hausaufgaben.trim() || anmerkungen.trim()) &&
        !(await confirm('Felder sind bereits ausgefüllt. Überschreiben?', { confirmLabel: 'Überschreiben' }))) return;

    try {
      const stream = await mikrofonAnfragen();
      const kandidaten = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
      const mime = kandidaten.find(m => MediaRecorder.isTypeSupported(m)) ?? '';
      let recorder: MediaRecorder;
      try { recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
      catch { recorder = new MediaRecorder(stream); }

      const chunks: Blob[] = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setDiktatLaeuft(false);
        setDiktatLaden(true);
        try {
          const blob = new Blob(chunks, { type: recorder.mimeType || mime || 'audio/webm' });
          const form = new FormData();
          form.append('audio', blob, 'aufnahme.webm');
          const tRes = await fetch(`${API_BASE}/api/transkribiere`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${holeToken() ?? ''}` },
            body: form,
          });
          if (!tRes.ok) throw new Error('Transkription fehlgeschlagen');
          const { text } = await tRes.json();

          const felder = await apiPost<any>('/api/klassenbuch/autofill', { text });
          if (felder.thema)        setThema(felder.thema);
          if (felder.hausaufgaben) setHausaufgaben(felder.hausaufgaben);
          if (felder.anmerkungen)  { setAnmerkungen(felder.anmerkungen); setAnmerkungOffen(true); }
          toast('Felder vorgeschlagen, bitte prüfen');
        } catch (e: any) {
          toast('Diktat fehlgeschlagen: ' + (e?.message ?? 'Fehler'), 'error');
        } finally {
          setDiktatLaden(false);
        }
      };

      diktatRecorderRef.current = recorder;
      recorder.start();
      setDiktatLaeuft(true);
    } catch (e: any) {
      zeigeMikrofonFehler(e, { stattdessenTippen: () => fokussiere('#ld-se-thema') });
    }
  }

  const mikro = (
    <button type="button" className={`ld-se-mikro${diktatLaeuft ? ' ist-an' : ''}`} onClick={startDiktat} disabled={diktatLaden}
      aria-label={diktatLaeuft ? 'Aufnahme stoppen' : 'Thema, Hausaufgabe und Anmerkung diktieren'}
      title={diktatLaeuft ? 'Aufnahme stoppen' : 'Thema, Hausaufgabe und Anmerkung diktieren'}>
      <MIcon name={diktatLaeuft ? 'stop' : diktatLaden ? 'hourglass_top' : 'mic'} size={19} />
    </button>
  );
  const fehlendListe = schuelerListe.filter(s => fehlend.has(s.id));
  const unterzeile = [wtKommaTm(datum), zeit, klasseName ? `Klasse ${klasseName}` : null].filter(Boolean).join(', ');

  const kopf = (
    <div className="ld-se-kopf">
      <div className="ld-se-kopf__text">
        <h2 className="ld-se-titel">
          <button ref={fachKnopf} type="button" className="ld-se-fach" aria-haspopup="menu" aria-expanded={fachMenu} onClick={() => setFachMenu(o => !o)}
            title="Fach ändern, zum Beispiel bei einer Vertretung">
            {fach.trim() || 'Fach wählen'}<MIcon name="expand_more" size={18} />
          </button>
          <span>, {stunde}. Stunde</span>
        </h2>
        <div className="ld-se-unter">{unterzeile}</div>
      </div>
      {!inline && <button type="button" className="ld-se-zu" aria-label="Schließen" onClick={onClose}><MIcon name="close" size={20} /></button>}
      {fachMenu && (
        <PortalMenu anker={fachKnopf.current} links onClose={() => setFachMenu(false)} label="Fach">
          {faecher.map(f => (
            <button key={f} type="button" role="menuitemradio" aria-checked={fach === f && !fachFrei} className="ld-kk-menu__item"
              onClick={() => { setFach(f); setFachFrei(false); setFachMenu(false); }}>
              {fach === f && !fachFrei ? <MIcon name="check" size={18} /> : <span style={{ width: 18 }} />}{f}
            </button>
          ))}
          <button type="button" role="menuitemradio" aria-checked={fachFrei} className="ld-kk-menu__item"
            onClick={() => { setFachFrei(true); if (faecher.includes(fach)) setFach(''); setFachMenu(false); setTimeout(() => fokussiere('#ld-se-fach-frei'), 50); }}>
            <MIcon name="edit" size={18} />Anderes Fach …
          </button>
        </PortalMenu>
      )}
    </div>
  );

  const felder = (
    <div className="ld-se-felder">
      {(fachFrei || faecher.length === 0) && (
        <div className="ld-se-feld">
          <label htmlFor="ld-se-fach-frei">Fach</label>
          <input id="ld-se-fach-frei" className="ld-se-input" value={fach} onChange={e => setFach(e.target.value)} placeholder="Fach eingeben, zum Beispiel Englisch (Vertretung)" />
        </div>
      )}
      <div className={inline ? 'ld-se-zwei' : undefined}>
        <div className="ld-se-feld">
          <label htmlFor="ld-se-thema">Thema</label>
          <div className="ld-se-mitmikro">
            <input id="ld-se-thema" className="ld-se-input" value={thema} onChange={e => setThema(e.target.value)} placeholder="Worum ging es?" />
            {mikro}
          </div>
        </div>
        <div className="ld-se-feld">
          <label htmlFor="ld-se-ha">Hausaufgabe</label>
          <div className="ld-se-mitmikro">
            <input id="ld-se-ha" className="ld-se-input" value={hausaufgaben} onChange={e => setHausaufgaben(e.target.value)} placeholder="Zum Beispiel Buch S. 45, Aufgabe 3" />
            {mikro}
          </div>
        </div>
      </div>
      {anmerkungOffen ? (
        <div className="ld-se-feld">
          <label htmlFor="ld-se-anm">Anmerkung</label>
          <textarea id="ld-se-anm" className="ld-se-input ld-se-textarea" rows={2} value={anmerkungen} onChange={e => setAnmerkungen(e.target.value)} placeholder="Besonderheiten, Störungen, Lob …" />
        </div>
      ) : (
        <button type="button" className="ld-kl-link ld-se-plus" onClick={() => { setAnmerkungOffen(true); setTimeout(() => fokussiere('#ld-se-anm'), 50); }}>
          <MIcon name="add" size={17} />Anmerkung
        </button>
      )}

      {/* Fehlend */}
      {schuelerListe.length > 0 && (
        <div className="ld-se-feld">
          <div className="ld-se-label">Fehlend <span className="ld-se-zahl">{fehlend.size}</span>{raster && <small>Kinder antippen, die fehlen</small>}</div>
          {raster ? (
            <>
              <div className="ld-se-raster" role="group" aria-label="Fehlende Kinder">
                {schuelerListe.map(s => (
                  <button key={s.id} type="button" aria-pressed={fehlend.has(s.id)} title={s.name}
                    className={`ld-se-kind${fehlend.has(s.id) ? (entschuldigt.has(s.id) ? ' ist-entsch' : ' ist-fehlt') : ''}`}
                    onClick={() => toggleFehlend(s.id)}>{kurzname(s.name)}</button>
                ))}
              </div>
              {fehlendListe.map(s => (
                <div key={s.id} className="ld-se-ue">
                  <span className={entschuldigt.has(s.id) ? undefined : 'ld-se-amber'}><b>{s.name}</b> fehlt</span>
                  <span className="ld-kl-umschalter" role="group" aria-label={`${s.name}: entschuldigt oder unentschuldigt`}>
                    <button type="button" aria-pressed={entschuldigt.has(s.id)} onClick={() => setzeEntschuldigt(s.id, true)}>entschuldigt</button>
                    <button type="button" aria-pressed={!entschuldigt.has(s.id)} onClick={() => setzeEntschuldigt(s.id, false)}>unentschuldigt</button>
                  </span>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="ld-se-chips">
                {fehlendListe.map(s => (
                  <button key={s.id} type="button" aria-haspopup="menu"
                    className={`ld-se-chip${entschuldigt.has(s.id) ? '' : ' ist-unentsch'}`}
                    onClick={e => setKindMenu({ id: s.id, el: e.currentTarget })}>
                    {s.name}, {entschuldigt.has(s.id) ? 'entschuldigt' : 'unentschuldigt'}<MIcon name="expand_more" size={17} />
                  </button>
                ))}
                <button type="button" className="ld-se-chip ld-se-chip--neu" aria-expanded={auswahlOffen} onClick={() => setAuswahlOffen(o => !o)}>
                  <MIcon name={auswahlOffen ? 'expand_less' : 'add'} size={17} />{auswahlOffen ? 'Auswahl schließen' : 'Fehlende wählen'}
                </button>
              </div>
              {auswahlOffen && (
                <div className="ld-se-raster ld-se-raster--handy" role="group" aria-label="Fehlende Kinder wählen">
                  {schuelerListe.map(s => (
                    <button key={s.id} type="button" aria-pressed={fehlend.has(s.id)}
                      className={`ld-se-kind${fehlend.has(s.id) ? (entschuldigt.has(s.id) ? ' ist-entsch' : ' ist-fehlt') : ''}`}
                      onClick={() => toggleFehlend(s.id)}>{s.name}</button>
                  ))}
                </div>
              )}
              {kindMenu && (
                <PortalMenu anker={kindMenu.el} links onClose={() => setKindMenu(null)} label="Fehlt">
                  <button type="button" role="menuitemradio" aria-checked={entschuldigt.has(kindMenu.id)} className="ld-kk-menu__item" onClick={() => { setzeEntschuldigt(kindMenu.id, true); setKindMenu(null); }}>
                    {entschuldigt.has(kindMenu.id) ? <MIcon name="check" size={18} /> : <span style={{ width: 18 }} />}entschuldigt
                  </button>
                  <button type="button" role="menuitemradio" aria-checked={!entschuldigt.has(kindMenu.id)} className="ld-kk-menu__item" onClick={() => { setzeEntschuldigt(kindMenu.id, false); setKindMenu(null); }}>
                    {!entschuldigt.has(kindMenu.id) ? <MIcon name="check" size={18} /> : <span style={{ width: 18 }} />}unentschuldigt
                  </button>
                  <button type="button" className="ld-kk-menu__item" onClick={() => { toggleFehlend(kindMenu.id); setKindMenu(null); }}>
                    <MIcon name="close" size={18} />Fehlt doch nicht
                  </button>
                </PortalMenu>
              )}
            </>
          )}
          {fehlend.size > 0 && <p className="ld-se-hinweis">Fehlende werden beim Speichern als Fehlzeit eingetragen.</p>}
        </div>
      )}

      {/* Welle B3: Mitarbeit (optional) */}
      {schuelerListe.length > 0 && !!klasse_id && !!fach.trim() && (
        <div className="ld-se-feld">
          <div className="ld-se-label">Mitarbeit diese Woche <small>optional{raster ? ', nur angetippte Kinder werden gespeichert' : ''}</small></div>
          <MitarbeitImEintrag z={mitarbeit} kinder={schuelerListe} fehlend={fehlend} fehlgeschlagen={mitarbeitFehler} zweispaltig={raster && inline} />
        </div>
      )}

      {fehler && <div role="alert" className="ld-se-fehler">{fehler}</div>}
      {loeschbar && (
        <button type="button" className="ld-kl-link ld-se-loeschen" onClick={eintragLoeschen}><MIcon name="delete" size={17} />Eintrag löschen</button>
      )}
    </div>
  );

  const fuss = (
    <div className={`ld-se-fuss${inline ? '' : ' ld-se-fuss--fest'}`}>
      {inline && mitWeiter && (
        <button type="button" className="ld-kl-link" disabled={laden || !fach.trim()} onClick={() => speichern('weiter')}>Speichern und nächste offene Stunde</button>
      )}
      <span className="ld-se-fuss__platz" />
      <button type="button" className="ld-kl-zweit" onClick={onClose}>Abbrechen</button>
      <button type="button" className="ld-kl-haupt" disabled={laden || !fach.trim()} onClick={() => speichern()}>{laden ? 'Speichern …' : 'Speichern'}</button>
    </div>
  );

  if (inline) return <div className="ld-se ld-se--inline">{kopf}{felder}{fuss}</div>;

  return (
    <LdSheet open={true} onClose={onClose} ariaLabel={`${fach || 'Stunde'}, ${stunde}. Stunde eintragen`} fullscreen={true}>
      <div className="ld-se ld-se--fenster">{kopf}{felder}{fuss}</div>
    </LdSheet>
  );
}
